import { SECOND_MS } from '@calm/web-common/dist/time';
import { Home } from '@mui/icons-material';
import Logo from '@rhg/../public/_n/images/logo-primary.svg';
import LogoWhite from '@rhg/../public/_n/images/logo-white.svg';
import commonMessages from '@rhg/lang/messages/common';
import {
    Box,
    Grow,
    Typography,
    Button,
    CircularProgress,
    useTheme,
    SxProps,
    Theme,
} from '@rhg/libs/material';
import { routes } from '@rhg/utils/routes';
import { useRouter } from 'next/router';
import { useEffect, useState, useMemo } from 'react';
import { useIntl } from 'react-intl';

const ANIMATION_DURATION = 2 * SECOND_MS;
const SUBTITLE_DISPLAY_TIMEOUT = SECOND_MS;
const DETAIL_DISPLAY_TIMEOUT = 1.7 * SECOND_MS;

const styleHomeButton = {
    mt: 5,
};

const styleLogoContainer = {
    mt: 2,
    mb: 4,
};

const styleBox: SxProps<Theme> = {
    background: (theme: Theme) => theme.palette.gradient.backgroundPrimary,
};

interface AnimatedScreenMessageProps {
    title: string;
    subtitle?: string;
    detail?: string;
    displayHomeButton?: boolean;
    isLoading?: boolean;
    displayLogo?: boolean;
}

const AnimatedScreenMessage = ({
    title,
    subtitle,
    detail,
    displayHomeButton = false,
    isLoading = false,
    displayLogo = false,
}: AnimatedScreenMessageProps) => {
    const { pathname } = useRouter();
    const { formatMessage } = useIntl();
    const [isSubtitleDisplayed, setIsSubtitleDisplayed] = useState(false);
    const [isDetailDisplayed, setIsDetailDisplayed] = useState(false);

    const {
        palette: { mode },
    } = useTheme();

    const isDarkMode = mode === 'dark';

    const logoProps = { width: '144px', height: '60px' };

    const isBackHomeButtonDisplayed = useMemo(
        () => displayHomeButton && pathname !== routes.dashboard,
        [displayHomeButton, pathname]
    );

    useEffect(() => {
        const subtitleTimer = setTimeout(
            () => setIsSubtitleDisplayed(true),
            SUBTITLE_DISPLAY_TIMEOUT
        );

        const detailTimer = setTimeout(
            () => setIsDetailDisplayed(true),
            DETAIL_DISPLAY_TIMEOUT
        );

        return () => {
            clearTimeout(subtitleTimer);
            clearTimeout(detailTimer);
        };
    }, []);

    return (
        <Box
            height="100dvh"
            display="flex"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            sx={styleBox}
        >
            {isLoading ? (
                <CircularProgress color="warning" size={100} thickness={2} />
            ) : (
                <>
                    {displayLogo && (
                        <Grow in timeout={ANIMATION_DURATION}>
                            <Box sx={styleLogoContainer}>
                                {isDarkMode ? (
                                    <LogoWhite {...logoProps} />
                                ) : (
                                    <Logo {...logoProps} />
                                )}
                            </Box>
                        </Grow>
                    )}
                    <Grow in timeout={ANIMATION_DURATION}>
                        <Typography
                            color="text.primary"
                            align="center"
                            variant="h3"
                        >
                            {title}
                        </Typography>
                    </Grow>
                    {subtitle && (
                        <Grow
                            in={isSubtitleDisplayed}
                            timeout={ANIMATION_DURATION}
                        >
                            <Typography
                                color="text.primary"
                                align="center"
                                variant="h6"
                            >
                                {subtitle}
                            </Typography>
                        </Grow>
                    )}
                    {detail && (
                        <Grow
                            in={isDetailDisplayed}
                            timeout={ANIMATION_DURATION}
                        >
                            <Typography
                                color="text.sub"
                                align="center"
                                variant="subtitle1"
                            >
                                {detail}
                            </Typography>
                        </Grow>
                    )}
                    {isBackHomeButtonDisplayed && (
                        <Grow
                            in={isSubtitleDisplayed}
                            timeout={ANIMATION_DURATION}
                        >
                            <Button
                                skipTracking
                                href={routes.dashboard}
                                startIcon={<Home />}
                                sx={styleHomeButton}
                                color="secondary"
                                variant="contained"
                            >
                                {formatMessage(
                                    commonMessages['common.back_home']
                                )}
                            </Button>
                        </Grow>
                    )}
                </>
            )}
        </Box>
    );
};
export default AnimatedScreenMessage;
